"use client";

import EverythingYouNeedSection from "@/app/(core)/components/EverythingYouNeedSection";
import MoreThanResourceSection from "@/app/(core)/components/MoreThanResourceSection";
import PageSection from "@/app/(core)/components/PageSection";
import PrimaryCtaButton from "@/app/(core)/components/PrimaryCtaButton";
import ReviewsSection from "@/app/(core)/components/ReviewsSection";
import SectionHeader from "@/app/(core)/components/SectionHeader";
import WaveBanner from "@/app/components/footer/components/WaveBanner";
import ResourceIcon from "@/app/components/logos/resources/ResourceIcon";
import type { Resources } from "@/types/subject-schema";
import {
  Avatar,
  Box,
  darken,
  Grid,
  Text,
  useComputedColorScheme,
  useMantineTheme,
} from "@mantine/core";
import ArrowForwardRounded from "@mui/icons-material/ArrowForwardRounded";
import CardGiftcardRounded from "@mui/icons-material/CardGiftcardRounded";
import EmojiEventsRounded from "@mui/icons-material/EmojiEventsRounded";
import FamilyRestroomRounded from "@mui/icons-material/FamilyRestroomRounded";
import PublicRounded from "@mui/icons-material/PublicRounded";
import RocketLaunchRounded from "@mui/icons-material/RocketLaunchRounded";
import SchoolRounded from "@mui/icons-material/SchoolRounded";
import StarRounded from "@mui/icons-material/StarRounded";
import ThumbUpRounded from "@mui/icons-material/ThumbUpRounded";
import TrendingUpRounded from "@mui/icons-material/TrendingUpRounded";
import Link from "next/link";
import CTACard from "./components/CTACard";
import StatCard from "./components/StatCard";
import TrustpilotIcon from "./components/TrustpilotIcon";
import classes from "./home-content.module.css";

export default function HomeContent() {
  const theme = useMantineTheme();
  const colorScheme = useComputedColorScheme("light");

  return (
    <main>
      {/* 1. Hero */}
      <PageSection
        variant="light"
        firstSection
        containerClassName={classes.heroContainer}
      >
        <SectionHeader
          badge={
            <Link
              href="https://uk.trustpilot.com/review/tibertutor.com"
              target="_blank"
              rel="noopener noreferrer"
              className={classes.heroBadgeLink}
            >
              <TrustpilotIcon width="18" height="18" />
              <TrustpilotIcon width="18" height="18" />
              <TrustpilotIcon width="18" height="18" />
              <TrustpilotIcon width="18" height="18" />
              Trustpilot
            </Link>
          }
          title="The Most Comprehensive IB STEM Platform"
          subTitle="Built by examiners, not AI. The only platform with integrated performance tracking."
          align="center"
          isFirstSection={true}
        />
        <Grid className={classes.heroStatsGrid} gutter="xl">
          <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
            <StatCard
              BackgroundIcon={PublicRounded}
              value="Global #1"
              description="Over 85,000 students. Across more than 130 countries."
              hoverIcons={HERO_FLAG_EMOJIS.map((emoji) => (
                <Text
                  key={emoji}
                  component="span"
                  role="img"
                  aria-label="Flag"
                  className={classes.heroFlagEmoji}
                >
                  {emoji}
                </Text>
              ))}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
            <StatCard
              BackgroundIcon={TrendingUpRounded}
              value="Top Results"
              description="Our users outperform the IB. We're the only site that tracks it."
              hoverIcons={HERO_SCORE_LABELS.map((score, index) => (
                <Text
                  key={score + index + "Score"}
                  component="span"
                  className={classes.heroScoreLabel}
                >
                  {score}
                </Text>
              ))}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
            <StatCard
              BackgroundIcon={ThumbUpRounded}
              value="100% Love"
              description={`Our members love us. We're "life changing" for them.`}
              hoverIcons={HERO_AVATAR_LETTERS.map((letter, index) => (
                <Box
                  key={letter + index + "Avatar"}
                  className={classes.heroBurstAvatar}
                >
                  <Avatar color="lightblue.0" radius="md" size="sm">
                    {letter}
                  </Avatar>
                  <Box className={classes.heroBurstStars}>
                    {[1, 2, 3, 4, 5].map((i) => (
                      <StarRounded
                        key={i}
                        style={{
                          width: 8,
                          height: 8,
                          fill: theme.colors.gold[5],
                          color: theme.colors.gold[5],
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              ))}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
            <StatCard
              BackgroundIcon={CardGiftcardRounded}
              value="7-Days Free"
              description="Instant access for everyone. No payment required."
              hoverIcons={HERO_RESOURCE_BURST.map((resource, index) => (
                <ResourceIcon
                  key={resource + index + "Resource"}
                  resource={resource}
                  size={28}
                  backgroundColor={
                    colorScheme === "dark"
                      ? theme.colors.dark[8]
                      : theme.colors.lightblue[1]
                  }
                />
              ))}
            />
          </Grid.Col>
        </Grid>
      </PageSection>
      <WaveBanner
        colors={{ light: theme.colors.gray[0], dark: theme.colors.dark[8] }}
        bgColor={
          colorScheme === "dark"
            ? darken(theme.colors.darkblue[9], 0.3)
            : theme.colors.darkblue[9]
        }
      />

      {/* 2. CTA */}
      <PageSection variant="dark">
        <SectionHeader
          title="Award-winning IB resources."
          align="center"
          needsLightTitle={true}
        />
        <Box className={classes.ctaAccolades}>
          <Link
            href="https://www.corporatevision-news.com/winners/tiber-tutor/"
            target="_blank"
            rel="noopener noreferrer"
            className={classes.ctaAccolade}
          >
            <EmojiEventsRounded className={classes.ctaAccoladeIcon} />
            <Text component="span" className={classes.ctaAccoladeText}>
              Leading IB Resource 2025
            </Text>
            <EmojiEventsRounded className={classes.ctaAccoladeIcon} />
          </Link>
          <Link
            href="https://www.corporatevision-news.com/winners/tiber-tutor/"
            target="_blank"
            rel="noopener noreferrer"
            className={classes.ctaAccolade}
          >
            <EmojiEventsRounded className={classes.ctaAccoladeIcon} />
            <Text component="span" className={classes.ctaAccoladeText}>
              STEM Excellence Award 2025
            </Text>
            <EmojiEventsRounded className={classes.ctaAccoladeIcon} />
          </Link>
        </Box>
        <Grid className={classes.ctaGrid} gutter="xl" align="stretch">
          <Grid.Col span={{ base: 12, md: 4 }}>
            <CTACard
              title="Get Started"
              description="Create an account and access leading IB resources instantly. Join 85,000+ students excelling worldwide."
              buttonText="Start Learning"
              href="/register"
              variant="primary"
              icon={<RocketLaunchRounded fontSize="large" />}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 4 }}>
            <CTACard
              title="I'm a Parent"
              description="Discover how Tiber can support your child's IB journey with expert resources, progress tracking, and proven results."
              buttonText="Learn More"
              href="/parents"
              variant="secondary"
              icon={<FamilyRestroomRounded fontSize="large" />}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 4 }}>
            <CTACard
              title="I'm a Student"
              description="See all the ways Tiber helps you excel in IB sciences. Share the benefits with your parents and bag yourself a membership."
              buttonText="Explore Benefits"
              href="/students"
              variant="secondary"
              icon={<SchoolRounded fontSize="large" />}
            />
          </Grid.Col>
        </Grid>
      </PageSection>
      <WaveBanner
        colors={{
          light: theme.colors.darkblue[9],
          dark: darken(theme.colors.darkblue[9], 0.3),
        }}
        bgColor={
          colorScheme === "dark"
            ? theme.colors.dark[8]
            : theme.colors.gray[0]
        }
      />

      {/* 3. Feature preview (deep dive) - scroll-driven tabs, gray bg */}
      <MoreThanResourceSection />
      <WaveBanner
        colors={{ light: theme.colors.gray[0], dark: theme.colors.dark[8] }}
        bgColor={
          colorScheme === "dark"
            ? darken(theme.colors.darkblue[9], 0.3)
            : theme.colors.darkblue[9]
        }
      />

      {/* 4. Resource overview - dark blue bg */}
      <EverythingYouNeedSection />
      <WaveBanner
        colors={{
          light: theme.colors.darkblue[9],
          dark: darken(theme.colors.darkblue[9], 0.3),
        }}
        bgColor={
          colorScheme === "dark"
            ? theme.colors.dark[8]
            : theme.colors.gray[0]
        }
      />

      {/* 5. Reviews */}
      <ReviewsSection />
      <WaveBanner
        colors={{ light: theme.colors.gray[0], dark: theme.colors.dark[8] }}
        bgColor={theme.colors.darkblue[9]}
      />

      {/* 6. Final CTA */}
      <PageSection variant="darkBottom" minHeight={false}>
        <SectionHeader
          title="Unlock Your Full Potential."
          subTitle="Gain unlimited access to award-winning resources used by 500+ schools across 130+ countries."
          align="center"
          needsLightTitle={true}
        />
        <PrimaryCtaButton href="/register" rightSection={<ArrowForwardRounded />}>
          Start your free trial
        </PrimaryCtaButton>
      </PageSection>
      <WaveBanner
        colors={{ light: theme.colors.blue[7], dark: theme.colors.darkblue[5] }}
        bgColor={
          colorScheme === "dark"
            ? theme.colors.dark[9]
            : darken(theme.colors.darkblue[9], 0.2)
        }
      />
    </main>
  );
}

const HERO_FLAG_EMOJIS = [
  "🇬🇧",
  "🇺🇸",
  "🇨🇦",
  "🇦🇺",
  "🇩🇪",
  "🇯🇵",
  "🇫🇷",
  "🇰🇷",
  "🇪🇸",
  "🇸🇪",
];
const HERO_SCORE_LABELS = [
  "85%",
  "86%",
  "88%",
  "89%",
  "90%",
  "91%",
  "92%",
  "93%",
  "95%",
  "96%",
];
const HERO_AVATAR_LETTERS = ["S", "J", "M", "A", "R", "K", "L", "P", "N", "D"];
const HERO_RESOURCE_BURST: Resources[] = [
  "videos",
  "notes",
  "flashcards",
  "questions",
  "cram sheets",
  "topic tests",
  "definitions",
  "ia examples",
  "syllabus",
  "videos",
  "notes",
  "flashcards",
];
